/**
 * PaperPatina — physics-only lighting overlay for photographic paper
 * cards (Feb 2026 · rev 3, "Photographic Realism Pass").
 *
 * The user's directive: match a real photographed investigator's desk.
 * That means NO illustrated overlays, NO decorative props, NO SVG
 * turbulence grain, NO dust flecks, NO stains, NO tape, NO stamps —
 * the paper PNG already carries real photographed fibres, worn edges,
 * and natural discolouration. The only thing this component adds is
 * a physically-motivated lamp lighting wash so every folder reads as
 * illuminated by the SAME overhead desk lamp:
 *
 *   • Warm highlight radiating from the upper-centre (where the lamp
 *     glow bathes the top of the sheet).
 *   • Deepening shadow toward the outer/lower edges (light falloff).
 *   • A subtle deterministic edge vignette so no two folders read
 *     identically — mimics the natural per-sheet variation that a
 *     photograph would capture but that no procedural PNG can carry.
 *
 * The component is intentionally silent for everything else. If real
 * wear is desired in the future it should come from more paper PNG
 * variants, not from CSS overlays.
 */
import React, { useMemo } from "react";

// -----------------------------------------------------------------------
// Deterministic PRNG (mulberry32 seeded from a string hash)
// -----------------------------------------------------------------------
function _hash(s) {
  let h = 1779033703 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
}
function _rng(seed) {
  let a = _hash(seed) || 0x9e3779b9;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296);
  };
}

// =======================================================================
// COMPONENT
// =======================================================================
// Props kept for backwards compatibility with existing call sites but
// most are no-ops now — the component always renders exactly two
// physically-motivated overlays.
export const PaperPatina = ({
  seed,
  variant = "dark",
  intensity = 0.7,
  className = "",
}) => {
  const isFolder = variant === "folder";

  const vignette = useMemo(() => {
    const rnd = _rng(seed || "default");
    // Small deterministic offset so no two folders share the same
    // edge shading — mimics per-sheet natural variation.
    return {
      x: 20 + Math.floor(rnd() * 60),
      y: 25 + Math.floor(rnd() * 55),
    };
  }, [seed]);

  // Folder-only wear: two softly darkened corners + one faint
  // handling crease, all deterministic per seed. Alphas stay very
  // low — believable edge wear, never distressed.
  const wear = useMemo(() => {
    const rnd = _rng(`${seed || "default"}-wear`);
    const corners = ["100% 0%", "0% 100%", "100% 100%", "0% 0%"];
    const i1 = Math.floor(rnd() * 4);
    let i2 = Math.floor(rnd() * 4);
    if (i2 === i1) i2 = (i2 + 1) % 4;
    return {
      c1: corners[i1],
      c2: corners[i2],
      creaseX: (26 + rnd() * 48).toFixed(1),
      creaseA: (87 + rnd() * 6).toFixed(1),
    };
  }, [seed]);

  // Two-layer overlay:
  //   1. Lamp light — warm radial highlight from upper-centre + soft
  //      falloff to shadow at the lower edges. This is what unifies
  //      every folder into "same photograph".
  //   2. Per-sheet edge vignette — a very light warm-brown gradient
  //      centred on a deterministic offset so each folder shades a
  //      little differently around its edges.
  // Both are pointer-events:none and use multiply so they DEEPEN the
  // real paper photograph beneath rather than overwriting it.
  return (
    <div
      aria-hidden="true"
      data-testid="paper-patina"
      className={`absolute inset-0 pointer-events-none select-none ${className}`}
      style={{
        mixBlendMode: "normal",
        opacity: intensity,
        borderRadius: "inherit",
        overflow: "hidden",
      }}
    >
      {/* --- 1 · Unified lamp lighting (physically motivated) --- */}
      <div style={{
        position: "absolute", inset: 0,
        background: `
          radial-gradient(ellipse 130% 90% at 50% -8%,
            rgba(255, 218, 150, 0.16) 0%,
            rgba(255, 205, 135, 0.06) 32%,
            rgba(0, 0, 0, 0) 62%),
          radial-gradient(ellipse 140% 110% at 50% 110%,
            rgba(20, 12, 5, 0.30) 0%,
            rgba(20, 12, 5, 0.12) 40%,
            rgba(0, 0, 0, 0) 72%)
        `,
        mixBlendMode: "multiply",
      }} />

      {/* --- 2 · Deterministic per-sheet edge vignette --- */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(ellipse at ${vignette.x}% ${vignette.y}%,
                     transparent 60%, rgba(45, 28, 12, 0.30) 120%)`,
        opacity: isFolder ? 0.28 : 0.36,
        mixBlendMode: "multiply",
      }} />

      {/* --- 3 · Folder-only corner wear + handling crease --- */}
      {isFolder && (
        <div style={{
          position: "absolute", inset: 0,
          background: `
            radial-gradient(circle 34px at ${wear.c1},
              rgba(52, 32, 12, 0.14), transparent 72%),
            radial-gradient(circle 24px at ${wear.c2},
              rgba(52, 32, 12, 0.10), transparent 75%),
            linear-gradient(${wear.creaseA}deg,
              transparent calc(${wear.creaseX}% - 0.7%),
              rgba(58, 38, 16, 0.06) ${wear.creaseX}%,
              rgba(255, 246, 220, 0.07) calc(${wear.creaseX}% + 0.45%),
              transparent calc(${wear.creaseX}% + 1.2%))
          `,
        }} />
      )}
    </div>
  );
};

export default PaperPatina;
